// The dashboard's date ranges, the sales-by-rep table and the store's daily
// target. The server's dashboard answers one day at a time, so a range adds up
// its days (mergeDashboards). Sales by rep come from the bills saved on this
// till (db.orders), since the server's dashboard doesn't split by rep: each
// line counts for its own rep, else the bill's, and returns take away.
import { TOP_STYLES } from "./dashboard";

export const DASHBOARD_RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 days" },
  { key: "month", label: "This month" },
];

const pad = (v) => String(v).padStart(2, "0");
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (iso, by) => {
  const [y, m, d] = iso.split("-").map(Number);
  return isoDay(new Date(y, m - 1, d + by));
};

// A range key → { from, to } as YYYY-MM-DD days, both ends in.
export function rangeDays(key, now = new Date()) {
  const today = isoDay(now);
  if (key === "yesterday") return { from: addDays(today, -1), to: addDays(today, -1) };
  if (key === "7d") return { from: addDays(today, -6), to: today };
  if (key === "month") return { from: `${today.slice(0, 8)}01`, to: today };
  return { from: today, to: today };
}

// Every day from..to, oldest first.
export function daysBetween(from, to) {
  const out = [];
  for (let d = from; d <= to && out.length < 400; d = addDays(d, 1)) out.push(d);
  return out;
}

const r2 = (v) => Math.round(v * 100) / 100;
const sumOrNull = (list, k) => (list.some((d) => d[k] != null) ? list.reduce((s, d) => s + (d[k] ?? 0), 0) : null);

// Several days of readDashboard output → one dashboard for the range. Averages
// are weighted by bills so they match what the server gives for a single day;
// the open work is the latest day's (it is "as it stands now", not per day).
export function mergeDashboards(days) {
  const list = (days || []).filter(Boolean);
  if (list.length === 0) return null;
  if (list.length === 1) return list[0];
  const bills = list.reduce((s, d) => s + d.bills, 0);
  const weighted = (k) => (bills > 0 ? r2(list.reduce((s, d) => s + (d[k] ?? 0) * d.bills, 0) / bills) : null);
  const products = new Map();
  const hours = new Map();
  for (const d of list) {
    for (const p of d.topProducts) {
      const g = products.get(p.productId) || { ...p, qty: 0, sales: 0 };
      g.qty += p.qty;
      g.sales += p.sales;
      products.set(p.productId, g);
    }
    for (const h of d.hours) {
      const g = hours.get(h.hour) || { hour: h.hour, bills: 0, sales: 0 };
      g.bills += h.bills;
      g.sales += h.sales;
      hours.set(h.hour, g);
    }
  }
  const latest = list[list.length - 1];
  return {
    ...latest,
    date: `${list[0].date}..${latest.date}`,
    bills,
    sales: r2(list.reduce((s, d) => s + d.sales, 0)),
    units: list.reduce((s, d) => s + d.units, 0),
    atv: weighted("atv"),
    upt: weighted("upt"),
    returns: list.reduce((s, d) => s + d.returns, 0),
    returnValue: r2(list.reduce((s, d) => s + d.returnValue, 0)),
    netSales: r2(list.reduce((s, d) => s + d.netSales, 0)),
    onlineOrders: sumOrNull(list, "onlineOrders"),
    onlineSales: sumOrNull(list, "onlineSales"),
    topProducts: [...products.values()].sort((a, b) => b.qty - a.qty || b.sales - a.sales).slice(0, TOP_STYLES * 4),
    hours: [...hours.values()].sort((a, b) => a.hour - b.hour),
  };
}

// A saved bill's orderTime ("DD-MM-YYYY HH:mm:ss", or ISO) → its YYYY-MM-DD day, or "".
export function orderDay(orderTime) {
  const s = String(orderTime || "").trim();
  let m = /^(\d{2})-(\d{2})-(\d{4})/.exec(s);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const t = Date.parse(s);
  return Number.isNaN(t) ? "" : isoDay(new Date(t));
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
export const NO_REP = "__none__";

// Saved bills (db.orders rows) → one row per rep for from..to, most net sales
// first: bills (sale bills the rep had a piece on), pieces and net sales (a
// returned piece takes away), and the average bill. Parked drafts are left out.
// repNames maps a rep id to a name when the bill didn't keep one.
export function salesByRep(orders, { from, to, repNames } = {}) {
  const reps = new Map();
  for (const o of orders || []) {
    if (!o || o.draftFlag === true || o.isCancelled === true) continue;
    const day = orderDay(o.orderTime);
    if (!day || (from && day < from) || (to && day > to)) continue;
    const onBill = new Set();
    for (const line of o.items || []) {
      if (!line) continue;
      const id = line.salesRepId || o.salesRepId || NO_REP;
      const back = line.isReturn === true || o.isReturn === true;
      const value = Math.abs(num(line.nettotal));
      const qty = Math.abs(num(line.weight ?? line.qty));
      if (value === 0 && qty === 0) continue;
      const sign = back ? -1 : 1;
      const r = reps.get(id) || { repId: id, name: "", bills: 0, pieces: 0, sales: 0 };
      if (!r.name) r.name = (id === line.salesRepId ? line.salesRepName : null) || (id === o.salesRepId ? o.salesRepName : null) || repNames?.get?.(id) || "";
      r.pieces += sign * qty;
      r.sales += sign * value;
      if (!back) onBill.add(id);
      reps.set(id, r);
    }
    for (const id of onBill) reps.get(id).bills += 1;
  }
  return [...reps.values()]
    .map((r) => ({ ...r, name: r.repId === NO_REP ? "No rep picked" : r.name || r.repId, sales: r2(r.sales), avgBill: r.bills > 0 ? r2(r.sales / r.bills) : null }))
    .sort((a, b) => b.sales - a.sales || a.name.localeCompare(b.name));
}

// Incentive rate (%): the back office's, when posConfig carries one, else the
// one typed on this till. Null when neither is set or it isn't a sane %.
export const INCENTIVE_CONFIG_KEYS = ["salesRepIncentivePercent", "salesRepCommissionPercent"];
export function configIncentiveRate(posConfig) {
  for (const k of INCENTIVE_CONFIG_KEYS) {
    const v = cleanRate(posConfig?.[k]);
    if (v != null) return v;
  }
  return null;
}
export function cleanRate(v) {
  if (v == null || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : null;
}
export const incentiveFor = (sales, rate) => (rate == null || sales <= 0 ? 0 : r2((sales * rate) / 100));

// Per-till settings kept on this browser. Storage can be blocked (private
// window, cleared data), so every read and write is guarded.
const keyFor = (what, tillId) => `fashion.dashboard.${what}.${tillId || "till"}`;
function readStored(what, tillId) {
  try {
    return globalThis.localStorage?.getItem(keyFor(what, tillId)) ?? null;
  } catch {
    return null;
  }
}
function writeStored(what, tillId, value) {
  try {
    if (value == null) globalThis.localStorage?.removeItem(keyFor(what, tillId));
    else globalThis.localStorage?.setItem(keyFor(what, tillId), String(value));
    return true;
  } catch {
    return false;
  }
}
export const readTillIncentiveRate = (tillId) => cleanRate(readStored("incentive", tillId));
export const saveTillIncentiveRate = (tillId, rate) => writeStored("incentive", tillId, cleanRate(rate));

export function cleanTarget(v) {
  if (v == null || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? r2(n) : null;
}
export const readDailyTarget = (tillId) => cleanTarget(readStored("target", tillId));
export const saveDailyTarget = (tillId, target) => writeStored("target", tillId, cleanTarget(target));

// The daily target over the range (a day's target times the days) against net sales.
export function targetProgress(netSales, dailyTarget, days = 1) {
  if (dailyTarget == null) return null;
  const target = r2(dailyTarget * Math.max(1, days));
  const pct = target > 0 ? Math.round((num(netSales) / target) * 100) : 0;
  return { target, pct, left: r2(Math.max(0, target - num(netSales))), met: num(netSales) >= target };
}
